import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { MessageCircle, Users, Clock, Award, MapPin, Anchor, Waves, LifeBuoy, UtensilsCrossed, Wine, Camera, Sun } from "lucide-react";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import { images } from "@/content/site";
import { WHATSAPP_LINK } from "@/content/site";
import { track } from "@/lib/tracking";
import { Header, TrustBar, Testimonials, Footer, FloatingWhatsApp } from "@/components/SitePage";
import CookieConsent from "@/components/CookieConsent";
import { useWhatsappGreeting } from "@/lib/whatsapp-greeting";

function WhatsAppCTA({
  source,
  variant = "green",
  label = "Book via WhatsApp",
  className = "",
}: {
  source: string;
  variant?: "green" | "outline" | "gold";
  label?: string;
  className?: string;
}) {
  const waMsg = useWhatsappGreeting("en");
  const base =
    "inline-flex items-center justify-center gap-2 px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors";
  const styles =
    variant === "green"
      ? "bg-[#25D366] text-[#001007] hover:bg-[#1ebe57]"
      : variant === "gold"
      ? "bg-gold text-emerald-deep hover:bg-[oklch(0.85_0.14_85)]"
      : "border border-white/60 text-white hover:border-gold hover:text-gold";
  return (
    <a
      href={WHATSAPP_LINK(waMsg)}
      target="_blank"
      rel="noopener noreferrer"
      data-cta-source={source}
      onClick={() => track("whatsapp_click", { source })}
      className={`${base} ${styles} ${className}`}
    >
      <MessageCircle size={14} />
      {label} →
    </a>
  );
}

function BenagilHero() {
  return (
    <section
      id="top"
      className="relative min-h-[92vh] flex items-center overflow-hidden bg-emerald-deep"
    >
      <div className="absolute inset-0">
        <ResponsiveImage
          picture={images.benagilInterior}
          alt="Algar de Benagil sea cave from boat view — sunlight through the cave oculus"
          sizes="100vw"
          priority
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/65" />
      </div>
      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 pt-32 pb-20 sm:pb-24">
        <p className="text-[11px] tracking-[0.32em] uppercase text-[oklch(0.95_0.01_95)] font-medium mb-6">
          Benagil Caves · Algarve, Portugal · Private Yacht from Vilamoura
        </p>
        <h1 className="font-display text-[2.4rem] leading-[1.05] sm:text-5xl lg:text-[4.5rem] text-[oklch(0.98_0.01_95)] max-w-[20ch] font-normal">
          Benagil Caves, Portugal — Private Tour from Vilamoura
          <span className="block italic text-gold font-normal mt-2 text-[1.6rem] sm:text-3xl lg:text-4xl">
            Private Boat Charter
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-base sm:text-lg text-[oklch(0.94_0.02_95)] leading-relaxed">
          Skip the crowds. Reach the famous <strong className="font-semibold text-white">Benagil caves on Portugal's Algarve coast</strong> by
          private yacht in 1h15 min, from Marina de Vilamoura. Up to 18 guests. Licensed skipper and
          deckhand included.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <WhatsAppCTA source="benagil-hero" variant="green" label="Book via WhatsApp" />
          <a
            href="#trip-details"
            className="inline-flex items-center justify-center px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] border border-white/60 text-white hover:border-gold hover:text-gold transition-colors"
          >
            See trip details
          </a>
        </div>
        <ul className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-[oklch(0.96_0.01_95)] text-sm">
          <li className="inline-flex items-center gap-2">
            <Users size={16} className="text-gold" /> Up to 18 guests
          </li>
          <li className="inline-flex items-center gap-2">
            <Clock size={16} className="text-gold" /> Departs Vilamoura Marina
          </li>
          <li className="inline-flex items-center gap-2">
            <Award size={16} className="text-gold" /> Licensed skipper included
          </li>
        </ul>
      </div>
    </section>
  );
}

function Section({
  id,
  eyebrow,
  title,
  children,
  bg = "white",
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  children: React.ReactNode;
  bg?: "white" | "sand";
}) {
  return (
    <section
      id={id}
      className={`${bg === "sand" ? "bg-sand" : "bg-white"} border-b border-emerald-deep/10`}
    >
      <div className="mx-auto max-w-4xl px-5 py-16 sm:py-20">
        {eyebrow && (
          <p className="eyebrow mb-3 text-gold-deep">{eyebrow}</p>
        )}
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
          {title}
        </h2>
        <div className="mt-6 space-y-5 text-base sm:text-lg text-foreground/80 leading-relaxed">
          {children}
        </div>
      </div>
    </section>
  );
}

function WhatIsBenagil() {
  return (
    <Section eyebrow="The Caves" title="The Benagil Caves of Portugal">
      <p>
        The <strong>Benagil caves</strong>, on Portugal's Algarve coast, are one of the most iconic sea cave
        systems in the world — carved by the Atlantic into the limestone cliffs next to Benagil beach in
        Lagoa. The headline cave, the <strong>Algar de Benagil</strong>, has a perfect golden sand floor
        beneath a natural domed ceiling, with a circular opening — the famous "oculus" — that pours
        sunlight down onto the cave floor at midday.
      </p>
      <p>
        The Benagil cave is only reachable from the sea. There is no land path: visitors come in by
        kayak, by stand-up paddleboard, or by boat. Since 2024, swimming inside Benagil is restricted
        and access is controlled by the local maritime authority — which is why arriving with an
        experienced operator matters.
      </p>
      <p>
        Around the Algar de Benagil, the same stretch of Algarve coast holds dozens more sea caves
        and rock arches: the Marinha cliffs, the Submarine cave, the Cathedral arch, Senhora da Rocha
        and Praia da Marinha — all part of the same Benagil caves system, and all worth seeing on the
        same private charter from Vilamoura.
      </p>
    </Section>
  );
}

function WhyPrivate() {
  const points = [
    {
      icon: <Users size={20} />,
      title: "Your group only",
      body:
        "A private yacht for up to 18 guests — no shared decks, no fixed seats, no strangers. The whole boat is yours.",
    },
    {
      icon: <Sun size={20} />,
      title: "Flexible departure",
      body:
        "Pick an early-morning slot to beat the crowds at Benagil and catch the best light through the oculus.",
    },
    {
      icon: <MapPin size={20} />,
      title: "More stops, fewer queues",
      body:
        "We add Marinha Beach, hidden coves and extra caves along the way — public group tours can't.",
    },
    {
      icon: <Waves size={20} />,
      title: "Snorkel & paddleboards",
      body:
        "Snorkel gear and stand-up paddleboards are on board so you can swim into smaller caves nearby.",
    },
  ];
  return (
    <section className="bg-sand border-b border-emerald-deep/10">
      <div className="mx-auto max-w-5xl px-5 py-16 sm:py-20">
        <p className="eyebrow mb-3 text-gold-deep">Why us</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
          Why a private charter to the Benagil caves from Vilamoura?
        </h2>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-foreground/80 leading-relaxed">
          Most Benagil caves tours in Portugal load 30+ strangers onto a rigid inflatable and rush you
          in and out. Morningstar is the opposite: a 200 m² private yacht, your group only, your pace.
        </p>
        <ul className="mt-10 grid sm:grid-cols-2 gap-6">
          {points.map((p, i) => (
            <li key={i} className="flex gap-4 bg-white p-6 border border-emerald-deep/10">
              <span className="text-gold-deep mt-1 shrink-0">{p.icon}</span>
              <div>
                <h3 className="font-display text-lg text-emerald-deep mb-1">{p.title}</h3>
                <p className="text-foreground/75 leading-relaxed text-[15px]">{p.body}</p>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-10">
          <WhatsAppCTA source="benagil-why" variant="green" label="Plan my Benagil charter" />
        </div>
      </div>
    </section>
  );
}

function Included() {
  const items = [
    { icon: <Anchor size={18} />, label: "Licensed skipper" },
    { icon: <Users size={18} />, label: "Deckhand on board" },
    { icon: <LifeBuoy size={18} />, label: "All safety equipment" },
    { icon: <Waves size={18} />, label: "Paddleboards" },
    { icon: <Waves size={18} />, label: "Snorkel gear" },
    { icon: <UtensilsCrossed size={18} />, label: "Light tapas" },
    { icon: <Wine size={18} />, label: "Drinks & water" },
    { icon: <Camera size={18} />, label: "Towels" },
  ];
  return (
    <section className="bg-white border-b border-emerald-deep/10">
      <div className="mx-auto max-w-5xl px-5 py-16 sm:py-20">
        <p className="eyebrow mb-3 text-gold-deep">Inclusions</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
          What's included in every Benagil caves tour
        </h2>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-foreground/80 leading-relaxed">
          One transparent price per group. No hidden extras, no fuel surcharge, no "tips not included".
        </p>
        <ul className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {items.map((it, i) => (
            <li
              key={i}
              className="flex items-center gap-2 bg-sand px-4 py-3 text-emerald-deep border border-emerald-deep/10 text-sm"
            >
              <span className="text-gold-deep shrink-0">{it.icon}</span>
              <span>{it.label}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function TheBoat() {
  return (
    <section className="bg-sand border-b border-emerald-deep/10">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="eyebrow mb-3 text-gold-deep">The Yacht</p>
          <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
            Morningstar — your private Benagil yacht
          </h2>
          <ul className="mt-6 space-y-3 text-foreground/80 text-base sm:text-lg">
            <li className="flex gap-3"><span className="text-gold-deep mt-1">›</span>200 m² of deck — up to 18 guests, in real comfort</li>
            <li className="flex gap-3"><span className="text-gold-deep mt-1">›</span>Shaded lounge, sun deck and bow sunbeds</li>
            <li className="flex gap-3"><span className="text-gold-deep mt-1">›</span>Marble bathroom and freshwater shower on the swim platform</li>
            <li className="flex gap-3"><span className="text-gold-deep mt-1">›</span>Tender used for the cave entry when sea state allows</li>
          </ul>
          <Link
            to="/the-morningstar"
            className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-emerald-deep border-b border-gold pb-1 hover:text-gold-deep transition-colors"
          >
            Discover the Morningstar →
          </Link>
        </div>
        <div className="aspect-[4/3] overflow-hidden">
          <ResponsiveImage
            picture={images.yachtAerial}
            alt="Morningstar private yacht — aerial view in the Algarve"
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    </section>
  );
}

function TripDetails() {
  const rows: Array<{ k: string; v: string }> = [
    { k: "Duration", v: "Half-day (4 to 5 h) or full-day (7 to 10 h)" },
    { k: "Departure", v: "Marina de Vilamoura, gate A2" },
    { k: "Best time", v: "Early morning — fewer boats at the Benagil caves and best light inside the oculus" },
    { k: "Group size", v: "Private charter, up to 18 guests" },
    { k: "Cancellation", v: "Free cancellation due to bad weather, decided by the captain." },
  ];
  return (
    <section id="trip-details" className="bg-white border-b border-emerald-deep/10">
      <div className="mx-auto max-w-4xl px-5 py-16 sm:py-20">
        <p className="eyebrow mb-3 text-gold-deep">Trip details</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
          Benagil caves tour — at a glance
        </h2>
        <dl className="mt-8 divide-y divide-emerald-deep/10 border-y border-emerald-deep/10">
          {rows.map((r) => (
            <div key={r.k} className="py-4 grid sm:grid-cols-[180px_1fr] gap-2">
              <dt className="text-[11px] uppercase tracking-[0.22em] text-emerald-deep/70 font-semibold pt-1">
                {r.k}
              </dt>
              <dd className="text-foreground/85 text-base">{r.v}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-10">
          <WhatsAppCTA source="benagil-trip" variant="green" label="Check availability" />
        </div>
      </div>
    </section>
  );
}

export const BENAGIL_FAQS = [
  {
    q: "Where are the Benagil caves in Portugal?",
    a: "The Benagil caves are on Portugal's central Algarve coast, next to Benagil beach in the municipality of Lagoa, between Albufeira and Carvoeiro. They're reachable only from the sea — by boat, kayak or paddleboard.",
  },
  {
    q: "How many Benagil caves are there?",
    a: "The Algar de Benagil is the famous one with the oculus, but the same stretch of Algarve coast holds dozens of sea caves and arches — Marinha, Submarine, Cathedral, Senhora da Rocha and more — which we visit on the same private charter from Vilamoura.",
  },
  {
    q: "Can you go inside the Benagil Cave on a yacht?",
    a: "Morningstar either anchors safely outside the cave and we transfer guests in a small tender to enter the cave or goes directly in, with favourable sea conditions. Tender entry is subject to sea state and local maritime authority rules.",
  },
  {
    q: "How long is a Benagil caves tour from Vilamoura?",
    a: "Our private Benagil day is 4, 5, 7 or 10 hours: roughly 1h15 sailing each way.",
  },
  {
    q: "What's the best time of day for the Benagil caves?",
    a: "Mid-morning departures hit Benagil with the sun directly over the famous oculus — best light for photos and calmest conditions. Afternoon is also fantastic as then you get the sunset back to Vilamoura. Your choice!",
  },
];

function FAQ() {
  return (
    <section className="bg-sand border-b border-emerald-deep/10">
      <div className="mx-auto max-w-3xl px-5 py-16 sm:py-20">
        <p className="eyebrow mb-3 text-gold-deep">FAQ</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-[1.1]">
          Benagil caves tour — frequently asked
        </h2>
        <dl className="mt-8 divide-y divide-emerald-deep/10 border-y border-emerald-deep/10">
          {BENAGIL_FAQS.map((f, i) => (
            <div key={i} className="py-5">
              <dt className="font-semibold text-emerald-deep">{f.q}</dt>
              <dd className="mt-2 text-foreground/75 leading-relaxed">{f.a}</dd>
            </div>
          ))}
        </dl>
        <div className="mt-10">
          <WhatsAppCTA source="benagil-faq" variant="green" label="Ask us directly" />
        </div>
        <div className="mt-6">
          <a href="/faq" className="inline-block text-[11px] uppercase tracking-[0.22em] text-gold-deep font-semibold hover:text-emerald-deep transition-colors">
            See the full 15-question FAQ →
          </a>
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-emerald-deep">
      <div className="mx-auto max-w-4xl px-5 py-20 sm:py-24 text-center">
        <h2 className="font-display text-3xl sm:text-4xl text-white leading-[1.1]">
          Ready to see the Benagil caves the right way?
        </h2>
        <p className="mt-5 text-[oklch(0.92_0.02_95)] max-w-xl mx-auto">
          Private yacht from Marina de Vilamoura to the Benagil caves on Portugal's Algarve coast. Up to
          18 guests. All-inclusive. Book directly with the crew on WhatsApp.
        </p>
        <div className="mt-8 flex justify-center">
          <WhatsAppCTA source="benagil-footer" variant="green" label="Book via WhatsApp" />
        </div>
      </div>
    </section>
  );
}

function StickyMobileCTA() {
  const [show, setShow] = useState(false);
  const waMsg = useWhatsappGreeting("en");
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <div
      className={`lg:hidden fixed inset-x-0 bottom-0 z-[55] bg-white border-t border-emerald-deep/10 px-4 py-3 transition-transform duration-300 ${show ? "translate-y-0" : "translate-y-full"}`}
      style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <a
        href={WHATSAPP_LINK(waMsg)}
        target="_blank"
        rel="noopener noreferrer"
        data-cta-source="benagil-sticky"
        onClick={() => track("whatsapp_click", { source: "benagil-sticky" })}
        className="flex items-center justify-center gap-2 w-full bg-[#25D366] text-[#001007] px-5 py-3.5 text-[12px] font-semibold uppercase tracking-[0.22em] hover:bg-[#1ebe57] transition-colors"
      >
        <MessageCircle size={16} /> Book Benagil caves tour →
      </a>
    </div>
  );
}

export default function BenagilLanding() {
  return (
    <>
      <Header lang="en" />
      <main>
        <BenagilHero />
        <TrustBar lang="en" />
        <WhatIsBenagil />
        <WhyPrivate />
        <Included />
        <TheBoat />
        <TripDetails />
        <FAQ />
        <FinalCTA />
        <Testimonials lang="en" />
      </main>
      <Footer lang="en" />
      <FloatingWhatsApp lang="en" />
      <StickyMobileCTA />
      <CookieConsent lang="en" />
    </>
  );
}